// Localização: frontend/src/pages/AgendaTarefas/AgendaTarefasPage.jsx (V0.2.1 - SearchContext)

import React, { useEffect, useState, useCallback, useMemo, useRef } from 'react';
import { Plus, Trash2, Edit, Repeat, User, Clock, CalendarCheck, Search, X, Info, XCircle, Calendar, Tag } from 'lucide-react';
import { useSearch } from '../../context/SearchContext';
import { useNotification } from '../../context/NotificationContext';
import { parseLocalDateTime } from '../../utils/formatters';

// Componentes UI e Modais
import PrimaryButton from '../../components/Common/PrimaryButton';
import ConfirmacaoModal from '../../components/Common/ConfirmacaoModal';
import LoadingIndicator from '../../components/Common/LoadingIndicator';
import Alert from '../../components/UI/Alert';
import PageTitle from '../../components/Layout/PageTitle';
import Modal from '../../components/UI/Modal';
import Card from '../../components/UI/Card';
import CardContent from '../../components/UI/CardContent';

// Formulários
import AgendaTarefaForm from '../../components/AgendaTarefas/AgendaTarefaForm'; 
import AgendaTarefaFormRecorrente from '../../components/AgendaTarefas/AgendaTarefaFormRecorrente';
import ModalCancelarTarefaRecorrente from '../../components/AgendaTarefas/ModalCancelarTarefaRecorrente';

// Filtros
import FloatingCalendarFilter from '../../components/Financeiro/FloatingCalendarFilter';
import TarefaStatusFilter from '../../components/AgendaTarefas/TarefaStatusFilter';
import TarefaTipoFilter from '../../components/AgendaTarefas/TarefaTipoFilter';
import FilterPaciente from '../../components/Pacientes/FilterPaciente'; 

// Hooks
import { useAgendaTarefas } from '../../hooks/useAgendaTarefas';
import { usePacienteList } from '../../hooks/usePacienteList';
import { getPacienteFotoUrl } from '../../utils/imageUtils';
import api from '../../api/api';


// Define a URL do avatar de fallback
const FALLBACK_AVATAR = '/images/higeia-avatar-shield.png';

// Mapeamento de Status para estilo
const statusStyles = {
    'Pendente': 'bg-yellow-100 text-yellow-800 border-yellow-300',
    'Em Andamento': 'bg-blue-100 text-blue-800 border-blue-300',
    'Concluída': 'bg-green-100 text-green-800 border-green-300',
    'Atrasada': 'bg-red-100 text-red-800 border-red-300',
    'Cancelada': 'bg-gray-100 text-gray-800 border-gray-300',
};

// Modal de Detalhes da Tarefa com componente Modal reutilizável (com efeito de desfoque)
const TarefaDetalhesModal = ({ isOpen, onClose, tarefa, getStatusReal }) => {
    // ⚠️ CRÍTICO: Hooks devem ser chamados ANTES de qualquer early return
    const { pacientes } = usePacienteList();

    if (!tarefa) return null;

    const paciente = pacientes.find(p => p.id === tarefa.paciente_id);
    const nomePaciente = tarefa.nome_paciente || paciente?.nome_completo || 'Não Atribuído';
    const statusReal = getStatusReal(tarefa);
    const statusClass = statusStyles[statusReal] || 'bg-gray-100 text-gray-800 border-gray-300';

    // Buscar nome do cuidador responsável se disponível
    const cuidador = tarefa.cuidador_responsavel_nome || tarefa.cuidador_nome || tarefa.profissional_nome || null;

    // Formatação de datas
    // CORREÇÃO: Usa parseLocalDateTime para evitar problemas de timezone (diferença de 3 horas)
    const formatDateTime = (date) => {
        if (!date) return 'Não informado';
        const dateObj = parseLocalDateTime(date);
        if (!dateObj) return 'Não informado';
        return dateObj.toLocaleString('pt-BR', { 
            weekday: 'long', 
            day: '2-digit', 
            month: 'long', 
            year: 'numeric',
            hour: '2-digit', 
            minute: '2-digit' 
        });
    };

    const formatTime = (date) => {
        if (!date) return 'Não informado';
        const dateObj = parseLocalDateTime(date);
        if (!dateObj) return 'Não informado';
        return dateObj.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' });
    };

    const dataAgendada = tarefa.hora_agendada ? parseLocalDateTime(tarefa.hora_agendada) : null;
    const dataConclusao = tarefa.hora_conclusao ? parseLocalDateTime(tarefa.hora_conclusao) : null;

    // Calcular diferença de tempo se tarefa foi concluída
    const tempoDecorrido = dataAgendada && dataConclusao 
        ? Math.round((dataConclusao - dataAgendada) / (1000 * 60)) // minutos
        : null;

    return (
        <Modal 
            isOpen={isOpen} 
            onClose={onClose} 
            title={
                <div className="flex items-center gap-2">
                    <Info className="text-emerald-600" size={24} />
                    <span>Detalhes da Tarefa</span>
                </div>
            }
            size="xl"
        >
            <div className="space-y-6">
                {/* Informações do Paciente */}
                <div className="bg-gradient-to-br from-emerald-50 to-emerald-100 rounded-xl p-5 border border-emerald-200">
                    <div className="flex items-center gap-4">
                        <img 
                            className="h-20 w-20 rounded-full object-cover border-4 border-white shadow-lg" 
                            src={getPacienteFotoUrl(paciente?.fotoURL)} 
                            alt={`Foto de ${nomePaciente}`} 
                            onError={(e) => {
                                e.target.onerror = null; 
                                e.target.src = FALLBACK_AVATAR; 
                            }}
                        />
                        <div className="flex-1">
                            <h3 className="text-2xl font-bold text-gray-900">{nomePaciente}</h3>
                            <p className="text-sm text-gray-600 mt-1">Paciente Atendido</p>
                            {paciente?.data_nascimento && (
                                <p className="text-xs text-gray-500 mt-1">
                                    Nascimento: {new Date(paciente.data_nascimento).toLocaleDateString('pt-BR')}
                                </p>
                            )}
                        </div>
                    </div>
                </div>

                {/* Informações Principais em Grid */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="bg-gray-50 rounded-lg p-4 border border-gray-200">
                        <label className="text-xs font-semibold text-gray-500 uppercase tracking-wide block mb-2">
                            Tipo de Tarefa
                        </label>
                        <p className="text-lg font-semibold text-gray-900">{tarefa.tipo_tarefa || 'Não especificado'}</p>
                    </div>
                    <div className="bg-gray-50 rounded-lg p-4 border border-gray-200">
                        <label className="text-xs font-semibold text-gray-500 uppercase tracking-wide block mb-2">
                            Status Atual
                        </label>
                        <div>
                            <span className={`px-4 py-2 inline-flex text-sm font-semibold rounded-full ${statusClass}`}>
                                {statusReal}
                            </span>
                        </div>
                    </div>
                </div>

                {/* Data e Hora Agendada */}
                {dataAgendada && (
                    <div className="bg-blue-50 rounded-lg p-4 border border-blue-200">
                        <div className="flex items-start gap-3">
                            <CalendarCheck className="text-blue-600 mt-0.5 flex-shrink-0" size={22} />
                            <div className="flex-1">
                                <label className="text-xs font-semibold text-gray-500 uppercase tracking-wide block mb-1">
                                    Data e Hora Agendada
                                </label>
                                <p className="text-base font-medium text-gray-900">{formatDateTime(dataAgendada)}</p>
                                <p className="text-xs text-gray-600 mt-1">
                                    Horário: {formatTime(dataAgendada)}
                                </p>
                            </div>
                        </div>
                    </div>
                )}

                {/* Data de Conclusão */}
                {dataConclusao && (
                    <div className="bg-green-50 rounded-lg p-4 border border-green-200">
                        <div className="flex items-start gap-3">
                            <Clock className="text-green-600 mt-0.5 flex-shrink-0" size={22} />
                            <div className="flex-1">
                                <label className="text-xs font-semibold text-gray-500 uppercase tracking-wide block mb-1">
                                    Data e Hora de Conclusão
                                </label>
                                <p className="text-base font-medium text-gray-900">{formatDateTime(dataConclusao)}</p>
                                {tempoDecorrido !== null && (
                                    <p className="text-xs text-gray-600 mt-1">
                                        Tempo decorrido: {tempoDecorrido > 60 
                                            ? `${Math.floor(tempoDecorrido / 60)}h ${tempoDecorrido % 60}min`
                                            : `${tempoDecorrido} minutos`}
                                    </p>
                                )}
                                {/* Informação de quem concluiu */}
                                {(cuidador || tarefa.id_cuidador_responsavel) && (
                                    <p className="text-xs text-gray-600 mt-2 flex items-center gap-1">
                                        <User className="h-3 w-3" />
                                        <span>
                                            Concluída por: <span className="font-semibold">{cuidador || `ID: ${tarefa.id_cuidador_responsavel}`}</span>
                                        </span>
                                    </p>
                                )}
                            </div>
                        </div>
                    </div>
                )}

                {/* Descrição da Tarefa */}
                <div className="bg-gray-50 rounded-lg p-4 border border-gray-200">
                    <label className="text-xs font-semibold text-gray-500 uppercase tracking-wide block mb-2">
                        Descrição da Tarefa
                    </label>
                    <p className="text-gray-900 leading-relaxed whitespace-pre-wrap">
                        {(() => {
                            let descricaoExibida = tarefa.descricao || 'Nenhuma descrição informada';
                            // Para tarefas de medicação, incluir o nome do medicamento na descrição se disponível
                            if (tarefa.tipo_tarefa === 'Medicação' && tarefa.nome_medicamento && tarefa.descricao) {
                                // Se a descrição não contém o nome do medicamento, adicionar
                                if (!tarefa.descricao.includes(tarefa.nome_medicamento)) {
                                    // Verificar se é o formato antigo "Administração de Medicamento (Plano X)"
                                    if (tarefa.descricao.includes('Administração de Medicamento')) {
                                        descricaoExibida = tarefa.descricao.replace('Administração de Medicamento', `Administração de ${tarefa.nome_medicamento}`);
                                    } else {
                                        // Se não for o formato esperado, adicionar o nome do medicamento no início
                                        descricaoExibida = `${tarefa.nome_medicamento} - ${tarefa.descricao}`;
                                    }
                                } else {
                                    descricaoExibida = tarefa.descricao;
                                }
                            }
                            return descricaoExibida === 'Nenhuma descrição informada' 
                                ? <span className="text-gray-400 italic">{descricaoExibida}</span>
                                : descricaoExibida;
                        })()}
                    </p>
                </div>

                {/* Observações de Conclusão */}
                {tarefa.observacoes_conclusao && (
                    <div className="bg-blue-50 rounded-lg p-4 border border-blue-200">
                        <label className="text-xs font-semibold text-gray-500 uppercase tracking-wide block mb-2">
                            Observações de Conclusão
                        </label>
                        <p className="text-gray-900 leading-relaxed whitespace-pre-wrap">{tarefa.observacoes_conclusao}</p>
                    </div>
                )}

                {/* Informações Adicionais */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 pt-4 border-t border-gray-200">
                    {tarefa.regra_id && (
                        <div className="flex items-center gap-2 bg-indigo-50 rounded-lg p-3 border border-indigo-200">
                            <Repeat className="text-indigo-600 flex-shrink-0" size={20} />
                            <div>
                                <p className="text-sm font-semibold text-indigo-900">Agendamento Recorrente</p>
                                <p className="text-xs text-indigo-700">ID da Regra: {tarefa.regra_id}</p>
                            </div>
                        </div>
                    )}
                    {cuidador && (
                        <div className="flex items-center gap-2 bg-blue-50 rounded-lg p-3 border border-blue-200">
                            <User className="text-blue-600 flex-shrink-0" size={20} />
                            <div>
                                <p className="text-sm font-semibold text-blue-900">Cuidador Responsável</p>
                                <p className="text-xs text-blue-700">{cuidador}</p>
                            </div>
                        </div>
                    )}
                    {!cuidador && tarefa.id_cuidador_responsavel && (
                        <div className="flex items-center gap-2 bg-gray-50 rounded-lg p-3 border border-gray-200">
                            <User className="text-gray-600 flex-shrink-0" size={20} />
                            <div>
                                <p className="text-sm font-semibold text-gray-900">Cuidador Responsável</p>
                                <p className="text-xs text-gray-600">ID: {tarefa.id_cuidador_responsavel}</p>
                            </div>
                        </div>
                    )}
                </div>

                {/* ID da Tarefa (informação técnica) */}
                <div className="pt-4 border-t border-gray-200">
                    <p className="text-xs text-gray-400 text-center">
                        ID da Tarefa: {tarefa.id}
                    </p>
                </div>
            </div>
        </Modal>
    );
};

// Componente de Card da Tarefa (seguindo o padrão do PacienteCard)
const TarefaCard = ({ tarefa, getStatusReal, onEdit, onDelete, onCancel, onViewDetails }) => {
    const { pacientes } = usePacienteList();
    
    // Encontrar paciente relacionado
    const paciente = pacientes.find(p => p.id === tarefa.paciente_id);
    const nomePaciente = tarefa.nome_paciente || paciente?.nome_completo || 'Não Atribuído';
    
    // Verificar se é tarefa recorrente (verifica regra_id de forma robusta)
    const isRecorrente = tarefa.regra_id !== null && tarefa.regra_id !== undefined && tarefa.regra_id !== 0 && tarefa.regra_id !== '';
    
    // Debug removido para evitar poluição do console
    
    // Formata a data e hora
    // CORREÇÃO: Usa parseLocalDateTime para evitar problemas de timezone (diferença de 3 horas)
    const formatTime = (date) => {
        if (!date) return '';
        return date.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' });
    };
    const dataAgendada = tarefa.hora_agendada ? parseLocalDateTime(tarefa.hora_agendada) : null;
    const formattedDate = dataAgendada 
        ? dataAgendada.toLocaleDateString('pt-BR', { 
            weekday: 'short', day: '2-digit', month: 'long' 
        })
        : 'Não agendado';
    const formattedTime = dataAgendada ? formatTime(dataAgendada) : '';

    const statusReal = getStatusReal(tarefa);
    const statusClass = statusStyles[statusReal] || 'bg-gray-100 text-gray-800 border-gray-300';

    return (
        // Card seguindo o padrão do PacienteCard: header com foto + corpo
        <div 
            className="bg-white rounded-xl shadow-md hover:shadow-xl transition-all duration-300 overflow-hidden border border-gray-200 hover:border-emerald-400 group cursor-pointer"
            onClick={() => onViewDetails(tarefa)}
        >
            {/* Header do Card com Foto */}
            <div className="relative bg-gradient-to-br from-emerald-50 to-emerald-100 p-4">
                <div className="flex items-center space-x-3">
                    {/* Foto do Paciente */}
                    <div className="flex-shrink-0">
                        <div className="relative">
                            <img 
                                className="h-16 w-16 rounded-full object-cover border-4 border-white shadow-md group-hover:scale-105 transition-transform duration-300" 
                                src={getPacienteFotoUrl(paciente?.fotoURL)} 
                                alt={`Foto de ${nomePaciente}`} 
                                onError={(e) => {
                                    e.target.onerror = null; 
                                    e.target.src = FALLBACK_AVATAR; 
                                }}
                            />
                            {/* Status Badge */}
                            <div className={`absolute -bottom-1 -right-1 h-5 w-5 rounded-full border-2 border-white ${
                                statusReal === 'Concluída' ? 'bg-green-500' :
                                statusReal === 'Atrasada' ? 'bg-red-500' :
                                statusReal === 'Em Andamento' ? 'bg-blue-500' :
                                'bg-yellow-500'
                            }`} title={statusReal}></div>
                        </div>
                    </div>
                    
                    {/* Nome do Paciente e Tipo de Tarefa */}
                    <div className="flex-1 min-w-0">
                        <h3 className="text-lg font-bold text-gray-900 truncate group-hover:text-emerald-700 transition-colors">
                            {nomePaciente}
                        </h3>
                        <p className="text-xs text-gray-600 truncate mt-1">
                            {tarefa.tipo_tarefa}
                        </p>
                    </div>
                </div>
            </div>

            {/* Corpo do Card */}
            <div className="p-4 space-y-3">
                {/* Informações */}
                <div className="space-y-2">
                    {/* Data e Horário */}
                    {dataAgendada && (
                        <div className="flex items-center text-sm text-gray-600">
                            <CalendarCheck className="h-4 w-4 mr-2 text-emerald-600 flex-shrink-0" />
                            <span className="truncate">
                                <span className="font-semibold">Agendada:</span> {formattedDate} às {formattedTime}
                            </span>
                        </div>
                    )}

                    {/* Tipo de Tarefa */}
                    <div className="flex items-center text-sm text-gray-600">
                        <Clock className="h-4 w-4 mr-2 text-emerald-600 flex-shrink-0" />
                        <span className="truncate">
                            <span className="font-semibold">Tipo:</span> {tarefa.tipo_tarefa}
                        </span>
                    </div>

                    {/* Status Badge e Etiqueta Recorrente */}
                    <div className="flex items-center justify-between pt-1 gap-2 flex-wrap">
                        <div className="flex items-center gap-2 flex-wrap">
                            <span className={`px-3 py-1 inline-flex text-xs font-semibold rounded-full ${statusClass}`}>
                                {statusReal}
                            </span>
                            {/* Etiqueta de Recorrente */}
                            {isRecorrente && (
                                <div className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-semibold border bg-indigo-100 text-indigo-800 border-indigo-300">
                                    <Repeat className="w-3.5 h-3.5" />
                                    <span>Recorrente</span>
                                </div>
                            )}
                        </div>
                    </div>
                </div>

                {/* Ações */}
                <div className="flex items-center justify-between pt-3 border-t border-gray-200">
                    {/* Espaço vazio à esquerda para manter consistência com PacienteCard */}
                    <div className="flex items-center space-x-2">
                        {/* Pode adicionar botões adicionais aqui no futuro se necessário */}
                    </div>

                    <div className="flex items-center space-x-2">
                        {/* Botão Editar */}
                        <button
                            onClick={(e) => {
                                e.stopPropagation();
                                onEdit(tarefa.id, !!tarefa.regra_id);
                            }}
                            disabled={tarefa.status === 'Concluída' || tarefa.status === 'Cancelada'}
                            className="p-2 text-emerald-600 hover:bg-emerald-50 rounded-lg transition-colors duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
                            title="Editar"
                        >
                            <Edit className="w-5 h-5" />
                        </button>
                        
                        {/* Botão Cancelar */}
                        {tarefa.status !== 'Concluída' && tarefa.status !== 'Cancelada' && (
                            <button
                                onClick={(e) => {
                                    e.stopPropagation();
                                    onCancel(tarefa);
                                }}
                                className="p-2 text-orange-600 hover:bg-orange-50 rounded-lg transition-colors duration-200"
                                title="Cancelar"
                            >
                                <XCircle className="w-5 h-5" />
                            </button>
                        )}
                        
                        {/* Botão Excluir */}
                        <button
                            onClick={(e) => {
                                e.stopPropagation();
                                onDelete(tarefa);
                            }}
                            disabled={tarefa.status === 'Concluída'}
                            className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
                            title="Excluir"
                        >
                            <Trash2 className="w-5 h-5" />
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
};

function AgendaTarefasPage() {
    
    // --- Hooks de Dados ---
    const { 
        tarefas, 
        fetchTarefasPendentes, 
        isLoadingTarefas, 
        deleteTarefa,
        cancelarTarefa,
        fetchTarefaById 
    } = useAgendaTarefas(null); 

    const { pacientes } = usePacienteList();
    const notification = useNotification();

    // Chave para localStorage
    const FILTROS_STORAGE_KEY = 'agendaTarefas_filtros';
    
    // Função para carregar filtros salvos
    const loadSavedFilters = () => {
        try {
            const saved = localStorage.getItem(FILTROS_STORAGE_KEY);
            if (saved) {
                const parsed = JSON.parse(saved);
                return {
                    filtroStatus: parsed.filtroStatus || [],
                    filtroTipo: parsed.filtroTipo || [],
                    rangeDatas: parsed.rangeDatas || { startDate: null, endDate: null },
                    pacienteFiltrado: parsed.pacienteFiltrado || null,
                    periodoFluxo: parsed.periodoFluxo || 'custom'
                };
            }
        } catch (error) {
            console.error('Erro ao carregar filtros salvos:', error);
        }
        return {
            filtroStatus: [],
            filtroTipo: [],
            rangeDatas: { startDate: null, endDate: null },
            pacienteFiltrado: null,
            periodoFluxo: 'custom'
        };
    };
    
    // Função para salvar filtros
    const saveFilters = (filtros) => {
        try {
            localStorage.setItem(FILTROS_STORAGE_KEY, JSON.stringify(filtros));
        } catch (error) {
            console.error('Erro ao salvar filtros:', error);
        }
    };
    
    // Carregar filtros salvos ao inicializar
    const savedFilters = loadSavedFilters();
    
    // --- Estados de Filtro ---
    const [filtroStatus, setFiltroStatus] = useState(savedFilters.filtroStatus); // Mudado para array para suportar múltiplos status
    const [filtroTipo, setFiltroTipo] = useState(savedFilters.filtroTipo); // Mudado para array para suportar múltiplos tipos
    const [rangeDatas, setRangeDatas] = useState(savedFilters.rangeDatas);
    const [pacienteFiltrado, setPacienteFiltrado] = useState(null); // Inicializa como null, será restaurado quando pacientes carregarem
    const [showFilterPaciente, setShowFilterPaciente] = useState(false); // Controla visibilidade do FilterPaciente
    
    // Estados para filtro de período flutuante
    const [periodoFluxo, setPeriodoFluxo] = useState(savedFilters.periodoFluxo);
    const [showPeriodFilter, setShowPeriodFilter] = useState(false);
    const periodFilterRef = useRef(null);
    const periodButtonRef = useRef(null);
    
    // Salvar filtros sempre que mudarem
    useEffect(() => {
        saveFilters({
            filtroStatus,
            filtroTipo,
            rangeDatas,
            pacienteFiltrado,
            periodoFluxo
        });
    }, [filtroStatus, filtroTipo, rangeDatas, pacienteFiltrado, periodoFluxo]);
    
    // 🎯 USANDO SEARCHCONTEXT: Substituir estado local por contexto global
    const { searchTerm, setSearchPlaceholder } = useSearch();

    // Configurar o placeholder de busca para esta página
    useEffect(() => {
        setSearchPlaceholder('Buscar por descrição ou paciente...');
    }, [setSearchPlaceholder]);

    // --- Estados de Modal e Ação (Inalterado) ---
    const [mostrarModalForm, setMostrarModalForm] = useState(false);
    const [mostrarModalRecorrente, setMostrarModalRecorrente] = useState(false); 
    const [alerta, setAlerta] = useState(null);
    
    // 🎯 ESTADOS DE EDIÇÃO
    const [tarefaParaEditar, setTarefaParaEditar] = useState(null);
    const [isEditing, setIsEditing] = useState(false);
    
    // 🛑 ESTADOS DE CONFIRMAÇÃO
    const [confirmacaoAcao, setConfirmacaoAcao] = useState({
        isOpen: false,
        titulo: "",
        mensagem: "",
        onConfirm: () => {}, 
        confirmText: "Confirmar",
        cancelText: "Cancelar",
    });
    
    // Armazenar a tarefa a ser excluída/selecionada para a exclusão
    const [tarefaSelecionadaParaExcluir, setTarefaSelecionadaParaExcluir] = useState(null);
    
    // Armazenar a tarefa a ser cancelada
    const [tarefaSelecionadaParaCancelar, setTarefaSelecionadaParaCancelar] = useState(null);
    const [motivoCancelamento, setMotivoCancelamento] = useState('');
    
    // Estados para modal de cancelamento/deleção recorrente
    const [showModalRecorrente, setShowModalRecorrente] = useState(false);
    const [tarefaRecorrente, setTarefaRecorrente] = useState(null);
    const [isDeletingRecorrente, setIsDeletingRecorrente] = useState(false);
    const [isLoadingRecorrente, setIsLoadingRecorrente] = useState(false);
    
    // Estado para modal de detalhes
    const [tarefaParaDetalhes, setTarefaParaDetalhes] = useState(null);
    const [mostrarModalDetalhes, setMostrarModalDetalhes] = useState(false);
    
    // Estados para botões flutuantes
    const [showNovaTarefaMenu, setShowNovaTarefaMenu] = useState(false);
    const novaTarefaMenuRef = useRef(null);
    const novaTarefaButtonRef = useRef(null);
    
    // Estados para filtro de status flutuante
    const [showStatusFilter, setShowStatusFilter] = useState(false);
    const statusFilterRef = useRef(null);
    const statusButtonRef = useRef(null);
    
    // Estados para filtro de tipo flutuante
    const [showTipoFilter, setShowTipoFilter] = useState(false);
    const tipoFilterRef = useRef(null);
    const tipoButtonRef = useRef(null);
    
    // Função auxiliar para formatar data localmente (sem conversão UTC)
    // Evita problemas de timezone que podem resultar em datas incorretas
    const formatLocalDate = useCallback((date) => {
        const year = date.getFullYear();
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const day = String(date.getDate()).padStart(2, '0');
        return `${year}-${month}-${day}`;
    }, []);

    // Função auxiliar para criar Date a partir de string "YYYY-MM-DD" em horário local
    // new Date("YYYY-MM-DD") interpreta como UTC, causando problemas de timezone
    const parseLocalDateString = useCallback((dateString) => {
        if (!dateString) return null;
        // Extrair componentes da string "YYYY-MM-DD"
        const match = String(dateString).trim().match(/^(\d{4})-(\d{2})-(\d{2})/);
        if (!match) return null;
        const [, year, month, day] = match;
        // Criar Date usando construtor local (mês é 0-indexed)
        return new Date(parseInt(year, 10), parseInt(month, 10) - 1, parseInt(day, 10));
    }, []);

    // Handler para mudança de período
    const handlePeriodoChange = useCallback((periodo) => {
        setPeriodoFluxo(periodo);
        
        // Se for 'custom', não recalcular datas (elas já foram definidas pelo onDateRangeChange)
        if (periodo === 'custom') {
            return;
        }

        const today = new Date();
        const start = new Date();
        const end = new Date();

        if (periodo === 'dia') {
            start.setHours(0, 0, 0, 0);
            end.setHours(23, 59, 59, 999);
        } else if (periodo === 'semana') {
            const dayOfWeek = today.getDay();
            start.setDate(today.getDate() - dayOfWeek);
            end.setDate(start.getDate() + 6);
        } else if (periodo === 'mes') {
            start.setDate(1);
            end.setMonth(end.getMonth() + 1);
            end.setDate(0);
        }

        setRangeDatas({
            startDate: formatLocalDate(start),
            endDate: formatLocalDate(end)
        });
    }, [formatLocalDate]);

    // Handler para mudança de range de datas
    const handleDateRangeChange = useCallback((start, end) => {
        setRangeDatas({
            startDate: start || null,
            endDate: end || null
        });
        setPeriodoFluxo('custom');
    }, []);

    // Restaurar paciente filtrado quando a lista de pacientes carregar
    useEffect(() => {
        if (savedFilters.pacienteFiltrado && pacientes.length > 0 && !pacienteFiltrado) {
            // Buscar o paciente salvo na lista atual de pacientes
            const pacienteRestaurado = pacientes.find(p => p.id === savedFilters.pacienteFiltrado?.id);
            if (pacienteRestaurado) {
                setPacienteFiltrado(pacienteRestaurado);
            }
        }
    }, [pacientes, savedFilters.pacienteFiltrado, pacienteFiltrado]);

    // 1. Efeito para carregar tarefas (Inalterado)
    useEffect(() => {
        fetchTarefasPendentes();
    }, [fetchTarefasPendentes]);

    // Fechar menu de Nova Tarefa ao clicar fora
    useEffect(() => {
        if (!showNovaTarefaMenu) return;

        const handleClickOutside = (event) => {
            if (
                novaTarefaMenuRef.current &&
                novaTarefaButtonRef.current &&
                !novaTarefaMenuRef.current.contains(event.target) &&
                !novaTarefaButtonRef.current.contains(event.target)
            ) {
                setShowNovaTarefaMenu(false);
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        document.addEventListener('touchstart', handleClickOutside);

        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
            document.removeEventListener('touchstart', handleClickOutside);
        };
    }, [showNovaTarefaMenu]);

    // Fechar menu ao pressionar Escape
    useEffect(() => {
        if (!showNovaTarefaMenu) return;

        const handleEscape = (event) => {
            if (event.key === 'Escape') {
                setShowNovaTarefaMenu(false);
            }
        };

        document.addEventListener('keydown', handleEscape);
        return () => {
            document.removeEventListener('keydown', handleEscape);
        };
    }, [showNovaTarefaMenu]);

    // Fechar dropdown de período ao clicar fora
    useEffect(() => {
        if (!showPeriodFilter) return;

        const handleClickOutside = (event) => {
            if (
                periodFilterRef.current &&
                periodButtonRef.current &&
                !periodFilterRef.current.contains(event.target) &&
                !periodButtonRef.current.contains(event.target)
            ) {
                setShowPeriodFilter(false);
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        document.addEventListener('touchstart', handleClickOutside);

        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
            document.removeEventListener('touchstart', handleClickOutside);
        };
    }, [showPeriodFilter]);

    // Fechar dropdown de período ao pressionar Escape
    useEffect(() => {
        if (!showPeriodFilter) return;

        const handleEscape = (event) => {
            if (event.key === 'Escape') {
                setShowPeriodFilter(false);
            }
        };

        document.addEventListener('keydown', handleEscape);
        return () => {
            document.removeEventListener('keydown', handleEscape);
        };
    }, [showPeriodFilter]);

    // Fechar dropdown de status ao clicar fora
    useEffect(() => {
        if (!showStatusFilter) return;

        const handleClickOutside = (event) => {
            if (
                statusFilterRef.current &&
                statusButtonRef.current &&
                !statusFilterRef.current.contains(event.target) &&
                !statusButtonRef.current.contains(event.target)
            ) {
                setShowStatusFilter(false);
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        document.addEventListener('touchstart', handleClickOutside);

        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
            document.removeEventListener('touchstart', handleClickOutside);
        };
    }, [showStatusFilter]);

    // Fechar dropdown de status ao pressionar Escape
    useEffect(() => {
        if (!showStatusFilter) return;

        const handleEscape = (event) => {
            if (event.key === 'Escape') {
                setShowStatusFilter(false);
            }
        };

        document.addEventListener('keydown', handleEscape);
        return () => {
            document.removeEventListener('keydown', handleEscape);
        };
    }, [showStatusFilter]);

    // Fechar dropdown de tipo ao clicar fora
    useEffect(() => {
        if (!showTipoFilter) return;

        const handleClickOutside = (event) => {
            if (
                tipoFilterRef.current &&
                tipoButtonRef.current &&
                !tipoFilterRef.current.contains(event.target) &&
                !tipoButtonRef.current.contains(event.target)
            ) {
                setShowTipoFilter(false);
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        document.addEventListener('touchstart', handleClickOutside);

        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
            document.removeEventListener('touchstart', handleClickOutside);
        };
    }, [showTipoFilter]);

    // Fechar dropdown de tipo ao pressionar Escape
    useEffect(() => {
        if (!showTipoFilter) return;

        const handleEscape = (event) => {
            if (event.key === 'Escape') {
                setShowTipoFilter(false);
            }
        };

        document.addEventListener('keydown', handleEscape);
        return () => {
            document.removeEventListener('keydown', handleEscape);
        };
    }, [showTipoFilter]);

    // Função para calcular o status real baseado no horário (não apenas no status do banco)
    // DEVE SER DEFINIDA ANTES DO FILTRO
    const getStatusReal = (tarefa) => {
        // Se já está concluída ou cancelada, mantém o status
        if (tarefa.status === 'Concluída' || tarefa.status === 'Cancelada' || tarefa.status === 'Em Andamento') {
            return tarefa.status;
        }
        
        // Para tarefas pendentes, verifica se realmente está atrasada
        // CORREÇÃO: Usa parseLocalDateTime para evitar problemas de timezone (diferença de 3 horas)
        if (tarefa.hora_agendada) {
            const agora = new Date();
            const horaAgendada = parseLocalDateTime(tarefa.hora_agendada);
            if (!horaAgendada) return 'Pendente';
            const diferencaMinutos = (agora - horaAgendada) / (1000 * 60); // Diferença em minutos
            
            // Só considera atrasada se passou mais de 15 minutos do horário agendado
            if (diferencaMinutos > 15) {
                return 'Atrasada';
            }
        }
        
        // Se não tem horário ou ainda não passou, mantém como Pendente
        return 'Pendente';
    };

    // Lógica de Filtro (CORRIGIDA para usar status real)
    const tarefasFiltradas = tarefas.filter((t) => {
        // Usa o status real calculado, não o status do banco
        const statusReal = getStatusReal(t);
        // Suporta tanto array (novo) quanto string (compatibilidade)
        const statusArray = Array.isArray(filtroStatus) ? filtroStatus : (filtroStatus ? [filtroStatus] : []);
        const correspondeStatus = statusArray.length === 0 || statusArray.includes(statusReal);
        // Suporta tanto array (novo) quanto string (compatibilidade)
        const tipoArray = Array.isArray(filtroTipo) ? filtroTipo : (filtroTipo ? [filtroTipo] : []);
        const correspondeTipo = tipoArray.length === 0 || tipoArray.includes(t.tipo_tarefa);
        // 🎯 USANDO SEARCHCONTEXT: Busca usando searchTerm do contexto
        const correspondeBusca = !searchTerm || 
            t.descricao?.toLowerCase().includes(searchTerm.toLowerCase()) || 
            t.nome_paciente?.toLowerCase().includes(searchTerm.toLowerCase());
        // Filtro por paciente selecionado
        const correspondePaciente = !pacienteFiltrado || t.paciente_id === pacienteFiltrado.id;
        
        // Filtro de datas: trata tanto null quanto strings vazias
        // CORREÇÃO: Compara strings de data diretamente para evitar problemas de timezone
        const startDateStr = rangeDatas.startDate && rangeDatas.startDate !== '' ? rangeDatas.startDate : null;
        const endDateStr = rangeDatas.endDate && rangeDatas.endDate !== '' ? rangeDatas.endDate : null;
        
        let correspondeDataInicio = true;
        let correspondeDataFim = true;
        
        // CORREÇÃO: Usa parseLocalDateTime para evitar problemas de timezone (diferença de 3 horas)
        if (t.hora_agendada) {
            const horaAgendada = parseLocalDateTime(t.hora_agendada);
            if (!horaAgendada) {
                correspondeDataInicio = false;
                correspondeDataFim = false;
            } else {
                // Extrair apenas a parte da data (YYYY-MM-DD) para comparação como string
                const dataAgendadaStr = formatLocalDate(horaAgendada);
                
                if (startDateStr) {
                    // Comparar strings diretamente (YYYY-MM-DD)
                    correspondeDataInicio = dataAgendadaStr >= startDateStr;
                }
                
                if (endDateStr) {
                    // Comparar strings diretamente (YYYY-MM-DD)
                    correspondeDataFim = dataAgendadaStr <= endDateStr;
                }
            }
        }

        return correspondeStatus && correspondeTipo && correspondeBusca && correspondeDataInicio && correspondeDataFim && correspondePaciente;
    });

    // Ordenar tarefas: Pendentes primeiro, depois Atrasadas, depois as demais
    const tarefasOrdenadas = useMemo(() => {
        return [...tarefasFiltradas].sort((a, b) => {
            const statusA = getStatusReal(a);
            const statusB = getStatusReal(b);
            
            // Prioridade: Pendente > Atrasada > Em Andamento > Concluída > Cancelada
            const prioridade = {
                'Pendente': 1,
                'Atrasada': 2,
                'Em Andamento': 3,
                'Concluída': 4,
                'Cancelada': 5
            };
            
            const prioridadeA = prioridade[statusA] || 99;
            const prioridadeB = prioridade[statusB] || 99;
            
            // Se tiverem prioridades diferentes, ordena por prioridade
            if (prioridadeA !== prioridadeB) {
                return prioridadeA - prioridadeB;
            }
            
            // Se tiverem a mesma prioridade, ordena por data/hora agendada (mais próximas primeiro)
            const dataA = a.hora_agendada ? parseLocalDateTime(a.hora_agendada) : null;
            const dataB = b.hora_agendada ? parseLocalDateTime(b.hora_agendada) : null;
            
            if (!dataA && !dataB) return 0;
            if (!dataA) return 1;
            if (!dataB) return -1;
            
            return dataA.getTime() - dataB.getTime();
        });
    }, [tarefasFiltradas, getStatusReal]);

    // --- Handlers de Modal e Ação (Manter inalterados para estabilidade) ---
    const handleFormCancel = useCallback(() => {
        setMostrarModalForm(false);
        setMostrarModalRecorrente(false);
        setTarefaParaEditar(null);
        setIsEditing(false);
    }, []);

    const handleOpenForm = (isRecorrente = false) => {
        handleFormCancel(); 
        setAlerta(null); 
        if (isRecorrente) {
            setMostrarModalRecorrente(true); 
        } else {
            setMostrarModalForm(true); 
        }
    }

    const handleEdit = useCallback(async (tarefaId, isRecorrente) => {
        setAlerta(null);
        handleFormCancel(); 
        
        try {
            const dadosCompletos = await fetchTarefaById(tarefaId);
            
            setTarefaParaEditar(dadosCompletos);
            setIsEditing(true);

            if (isRecorrente) {
                setMostrarModalRecorrente(true);
            } else {
                setMostrarModalForm(true);
            }

        } catch (error) {
            console.error('Erro ao preparar edição:', error);
            setAlerta({ 
                tipo: 'error', 
                mensagem: error.message || 'Falha ao carregar dados da tarefa para edição.' 
            });
        }
    }, [fetchTarefaById, handleFormCancel]);


    const handleOpenConfirmacaoAcao = useCallback(({ isEditing, onConfirm, tipo = 'simples', dataInicio, horarioBase, horaAgendada, paciente_id }) => {
        
        console.log('[AgendaTarefasPage] handleOpenConfirmacaoAcao chamado com:', {
            tipo,
            paciente_id,
            tipo_paciente_id: typeof paciente_id,
            total_pacientes: pacientes.length
        });
        
        const isRecorrente = tipo === 'recorrente';
        
        // Validar se paciente_id foi fornecido (incluindo string vazia)
        if (!paciente_id || paciente_id === '' || paciente_id === '0' || paciente_id === 0) {
            console.error('Erro: paciente_id não fornecido na confirmação. Valor:', paciente_id, 'Tipo:', typeof paciente_id);
            setAlerta({ 
                tipo: 'error', 
                mensagem: 'Erro: Paciente não selecionado. Por favor, selecione um paciente antes de confirmar.' 
            });
            // Não fecha o modal, apenas mostra o erro
            if (isRecorrente) {
                setMostrarModalRecorrente(true);
            } else {
                setMostrarModalForm(true);
            }
            return;
        }
        
        // Obter nome do paciente - comparação robusta que funciona com string ou number
        const paciente = pacientes.find(p => {
            // Converte ambos para string e número para comparação robusta
            const pIdStr = String(p.id).trim();
            const pacienteIdStr = String(paciente_id).trim();
            const pIdNum = Number(p.id);
            const pacienteIdNum = Number(paciente_id);
            
            const match = pIdStr === pacienteIdStr || (pIdNum === pacienteIdNum && !isNaN(pIdNum) && !isNaN(pacienteIdNum));
            if (match) {
                console.log('[AgendaTarefasPage] Paciente encontrado:', { id: p.id, nome: p.nome_completo });
            }
            return match;
        });
        
        // Se não encontrou o paciente, exibir erro e não prosseguir
        if (!paciente) {
            console.error('Paciente não encontrado na lista. ID fornecido:', paciente_id, 'Tipo:', typeof paciente_id);
            console.error('IDs disponíveis na lista:', pacientes.map(p => ({ id: p.id, tipo: typeof p.id, nome: p.nome_completo })));
            setAlerta({ 
                tipo: 'error', 
                mensagem: `Erro: Paciente com ID ${paciente_id} não foi encontrado na lista. Por favor, recarregue a página.` 
            });
            // Não fecha o modal, apenas mostra o erro
            if (isRecorrente) {
                setMostrarModalRecorrente(true);
            } else {
                setMostrarModalForm(true);
            }
            return;
        }
        
        const nomePaciente = paciente.nome_completo;
        console.log('[AgendaTarefasPage] Nome do paciente obtido:', nomePaciente);
        
        // Formatador de data/hora para tarefas simples
        const formatarDataHoraSimples = (dateTimeLocalString) => {
            if (!dateTimeLocalString) return 'Data não definida';
            try {
                // O formato datetime-local vem como "YYYY-MM-DDTHH:mm"
                // Precisamos garantir que seja interpretado como hora local
                let date;
                if (dateTimeLocalString.includes('T')) {
                    // Se já tem T, pode ser ISO ou datetime-local
                    // Se não tem 'Z' ou offset, assumimos que é local
                    if (!dateTimeLocalString.includes('Z') && !dateTimeLocalString.match(/[+-]\d{2}:\d{2}$/)) {
                        // É datetime-local, precisamos adicionar o offset para interpretar corretamente
                        // Mas para formatação, podemos usar diretamente
                        const [datePart, timePart] = dateTimeLocalString.split('T');
                        const [year, month, day] = datePart.split('-').map(Number);
                        const [hour, minute] = timePart.split(':').map(Number);
                        date = new Date(year, month - 1, day, hour, minute);
                    } else {
                        date = new Date(dateTimeLocalString);
                    }
                } else {
                    date = new Date(dateTimeLocalString);
                }
                
                // Verificar se a data é válida
                if (isNaN(date.getTime())) {
                    return 'Data inválida';
                }
                
                const dataFormatada = date.toLocaleDateString('pt-BR', {
                    weekday: 'short',
                    day: '2-digit',
                    month: 'long',
                    year: 'numeric'
                });
                const horaFormatada = date.toLocaleTimeString('pt-BR', {
                    hour: '2-digit',
                    minute: '2-digit'
                });
                return `${dataFormatada} às ${horaFormatada}`;
            } catch (error) {
                console.error('Erro ao formatar data:', error);
                return 'Data inválida';
            }
        };
        
        // Formatador de data/hora para tarefas recorrentes
        const formatarDataHoraRecorrente = (dataInicio, horarioBase) => {
            if (!dataInicio || !horarioBase) return 'Data não definida';
            try {
                const date = new Date(dataInicio);
                const dataFormatada = date.toLocaleDateString('pt-BR', {
                    weekday: 'short',
                    day: '2-digit',
                    month: 'long',
                    year: 'numeric'
                });
                return `${dataFormatada} às ${horarioBase}`;
            } catch (error) {
                return 'Data inválida';
            }
        };

        // Montar a mensagem formatada
        let mensagemFormatada = '';
        if (isEditing) {
            mensagemFormatada = `Você está prestes a salvar as alterações da tarefa para o paciente **${nomePaciente}**. ${isRecorrente ? 'Para Regras Recorrentes, isso afetará **todas as futuras ocorrências**. ' : ''}Deseja continuar?`;
        } else {
            const dataHoraFormatada = isRecorrente 
                ? formatarDataHoraRecorrente(dataInicio, horarioBase)
                : formatarDataHoraSimples(horaAgendada);
            
            if (isRecorrente) {
                mensagemFormatada = `Deseja realmente criar esta Regra de Agendamento para o paciente **${nomePaciente}**? A primeira ocorrência será em: **${dataHoraFormatada}**.`;
            } else {
                mensagemFormatada = `Deseja realmente agendar a tarefa para o paciente **${nomePaciente}** em: **${dataHoraFormatada}**?`;
            }
        }

        if (isRecorrente) {
            setMostrarModalRecorrente(false);
        } else {
            setMostrarModalForm(false);
        }

        setConfirmacaoAcao({
            isOpen: true,
            titulo: isEditing 
                ? (isRecorrente ? "Confirmar Edição de Regra Recorrente" : "Confirmar Edição de Tarefa")
                : (isRecorrente ? "Confirmar Agendamento Recorrente" : "Confirmar Criação de Tarefa"),
            mensagem: mensagemFormatada,
            onConfirm: () => {
                onConfirm();
                setConfirmacaoAcao(prev => ({ ...prev, isOpen: false })); 
            },
            confirmText: isEditing ? "Salvar Alterações" : "Confirmar Agendamento",
            cancelText: "Voltar ao Formulário",
        });
    }, [pacientes]);

    const handleCloseConfirmacaoAcao = useCallback(() => {
        if (confirmacaoAcao.confirmText.includes("Salvar") || confirmacaoAcao.confirmText.includes("Confirmar Agendamento")) {
            if (tarefaParaEditar?.regra_id || mostrarModalRecorrente) {
                setMostrarModalRecorrente(true);
            } else {
                setMostrarModalForm(true);
            }
        }
        // Limpar estados relacionados quando fechar
        setTarefaSelecionadaParaExcluir(null);
        setTarefaSelecionadaParaCancelar(null);
        setMotivoCancelamento('');
        setConfirmacaoAcao(prev => ({ ...prev, isOpen: false }));
    }, [confirmacaoAcao.confirmText, tarefaParaEditar, mostrarModalRecorrente]);


    const handleExcluirTarefa = useCallback(async () => {
        if (!tarefaSelecionadaParaExcluir) return;
        setConfirmacaoAcao(prev => ({ ...prev, isOpen: false })); 
        setAlerta(null);

        try {
            await deleteTarefa(tarefaSelecionadaParaExcluir.id);
            setAlerta({ tipo: 'success', mensagem: 'Tarefa excluída com sucesso.' });
            setTarefaSelecionadaParaExcluir(null);
            fetchTarefasPendentes(); 
        } catch (error) {
            console.error('Erro ao excluir tarefa:', error);
            setAlerta({ 
                tipo: 'error', 
                mensagem: error.response?.data?.message || 'Falha ao excluir a tarefa.' 
            });
        }
    }, [tarefaSelecionadaParaExcluir, deleteTarefa, fetchTarefasPendentes]);

    const handlePreparaExclusao = useCallback((tarefa) => {
        // Verificar se é tarefa recorrente
        const isRecorrente = tarefa.regra_id !== null && tarefa.regra_id !== undefined && tarefa.regra_id !== 0 && tarefa.regra_id !== '';
        
        if (isRecorrente) {
            // Fechar modal de confirmação simples se estiver aberto
            setConfirmacaoAcao(prev => ({ ...prev, isOpen: false }));
            // Limpar estados relacionados
            setTarefaSelecionadaParaExcluir(null);
            setTarefaSelecionadaParaCancelar(null);
            // Usar modal de deleção recorrente
            setTarefaRecorrente(tarefa);
            setIsDeletingRecorrente(true);
            setShowModalRecorrente(true);
        } else {
            // Fechar modal recorrente se estiver aberto
            setShowModalRecorrente(false);
            setTarefaRecorrente(null);
            setIsDeletingRecorrente(false);
            // Limpar estados relacionados
            setTarefaSelecionadaParaCancelar(null);
            setMotivoCancelamento('');
            // Usar modal simples
            setTarefaSelecionadaParaExcluir(tarefa);
            setConfirmacaoAcao({
                isOpen: true,
                titulo: "Excluir Tarefa",
                mensagem: `Tem certeza que deseja excluir a tarefa "${tarefa.descricao || tarefa.tipo_tarefa}"? Esta ação não pode ser desfeita.`,
                onConfirm: handleExcluirTarefa,
                confirmText: "Sim, excluir",
                cancelText: "Não, manter",
            });
        }
    }, [handleExcluirTarefa]);

    const handleCancelarTarefa = useCallback(async () => {
        if (!tarefaSelecionadaParaCancelar || !motivoCancelamento.trim()) {
            setAlerta({ tipo: 'error', mensagem: 'Por favor, informe o motivo do cancelamento.' });
            return;
        }

        setConfirmacaoAcao(prev => ({ ...prev, isOpen: false }));
        setAlerta(null);

        try {
            await cancelarTarefa(tarefaSelecionadaParaCancelar.id, motivoCancelamento);
            setAlerta({ tipo: 'success', mensagem: 'Tarefa cancelada com sucesso.' });
            setTarefaSelecionadaParaCancelar(null);
            setMotivoCancelamento('');
            fetchTarefasPendentes();
        } catch (error) {
            console.error('Erro ao cancelar tarefa:', error);
            setAlerta({
                tipo: 'error',
                mensagem: error.message || 'Falha ao cancelar a tarefa.'
            });
        }
    }, [tarefaSelecionadaParaCancelar, motivoCancelamento, cancelarTarefa, fetchTarefasPendentes]);

    // Handler para confirmar cancelamento/deleção recorrente
    const handleConfirmRecorrente = useCallback(async ({ opcao, motivo, confirmar_todas }) => {
        if (!tarefaRecorrente) return;

        setIsLoadingRecorrente(true);
        setAlerta(null);

        try {
            if (isDeletingRecorrente) {
                // Lógica de deleção
                if (opcao === 'apenas_esta') {
                    await deleteTarefa(tarefaRecorrente.id);
                    setAlerta({ tipo: 'success', mensagem: 'Tarefa excluída com sucesso.' });
                } else if (opcao === 'todas_futuras') {
                    await api.delete(`/agenda-tarefas/recorrente/${tarefaRecorrente.regra_id}`, {
                        data: { opcao: 'todas_futuras' }
                    });
                    setAlerta({ tipo: 'success', mensagem: 'Tarefas futuras excluídas com sucesso. A regra foi desativada para parar a criação automática.' });
                }
            } else {
                // Lógica de cancelamento
                if (opcao === 'apenas_esta') {
                    await cancelarTarefa(tarefaRecorrente.id, motivo);
                    setAlerta({ tipo: 'success', mensagem: 'Tarefa cancelada com sucesso.' });
                } else if (opcao === 'todas_futuras') {
                    await api.put(`/agenda-tarefas/cancelar/recorrente/${tarefaRecorrente.regra_id}`, {
                        opcao: 'todas_futuras',
                        motivo_cancelamento: motivo
                    });
                    setAlerta({ tipo: 'success', mensagem: 'Tarefas futuras canceladas com sucesso. A regra foi desativada para parar a criação automática.' });
                }
            }
            
            setShowModalRecorrente(false);
            setTarefaRecorrente(null);
            fetchTarefasPendentes();
        } catch (error) {
            console.error(`Erro ao ${isDeletingRecorrente ? 'deletar' : 'cancelar'} tarefa recorrente:`, error);
            setAlerta({
                tipo: 'error',
                mensagem: error.response?.data?.message || `Falha ao ${isDeletingRecorrente ? 'deletar' : 'cancelar'} a tarefa.`
            });
        } finally {
            setIsLoadingRecorrente(false);
        }
    }, [tarefaRecorrente, isDeletingRecorrente, deleteTarefa, cancelarTarefa, fetchTarefasPendentes]);

    const handlePreparaCancelamento = useCallback((tarefa) => {
        // Verificar se é tarefa recorrente
        const isRecorrente = tarefa.regra_id !== null && tarefa.regra_id !== undefined && tarefa.regra_id !== 0 && tarefa.regra_id !== '';
        
        if (isRecorrente) {
            // Fechar qualquer modal de confirmação que esteja aberto
            setConfirmacaoAcao(prev => ({ ...prev, isOpen: false }));
            // Usar modal de cancelamento recorrente
            setTarefaRecorrente(tarefa);
            setIsDeletingRecorrente(false);
            setShowModalRecorrente(true);
        } else {
            // Fechar modal recorrente se estiver aberto
            setShowModalRecorrente(false);
            setTarefaRecorrente(null);
            // Usar modal simples
            setTarefaSelecionadaParaCancelar(tarefa);
            setMotivoCancelamento('');
            setConfirmacaoAcao({
                isOpen: true,
                titulo: "Cancelar Tarefa",
                mensagem: `Deseja cancelar a tarefa "${tarefa.descricao || tarefa.tipo_tarefa}"? Por favor, informe o motivo do cancelamento:`,
                onConfirm: handleCancelarTarefa,
                confirmText: "Sim, cancelar",
                cancelText: "Não",
                showMotivoInput: true,
                motivoValue: motivoCancelamento,
                onMotivoChange: setMotivoCancelamento
            });
        }
    }, [handleCancelarTarefa, motivoCancelamento]);

    const handleFormSuccess = useCallback(() => {
        // Capturar o estado ANTES de fechar o modal para evitar problemas de closure
        const wasEditing = !!tarefaParaEditar;
        
        // Mostrar notificação toast ANTES de fechar o modal
        const mensagem = wasEditing ? 'Tarefa atualizada com sucesso!' : 'Tarefa criada com sucesso!';
        
        console.log('[AgendaTarefasPage] handleFormSuccess chamado', {
            wasEditing,
            tarefaParaEditar: !!tarefaParaEditar,
            mensagem,
            notificationAvailable: !!notification,
            successAvailable: !!(notification && notification.success)
        });
        
        // Chamar notificação imediatamente
        if (notification && notification.success) {
            notification.success(mensagem, 4000);
            console.log('[AgendaTarefasPage] Notificação chamada');
        } else {
            console.warn('[AgendaTarefasPage] notification.success não disponível, usando fallback');
        }
        
        // Fechar modal e limpar estados (isso também limpa tarefaParaEditar e isEditing)
        // Usar handleFormCancel para garantir consistência
        handleFormCancel();
        
        // Atualizar a lista de tarefas
        fetchTarefasPendentes(); 
    }, [tarefaParaEditar, notification, fetchTarefasPendentes, handleFormCancel]);

    const handleViewDetails = useCallback((tarefa) => {
        setTarefaParaDetalhes(tarefa);
        setMostrarModalDetalhes(true);
    }, []);

    const handleCloseDetalhes = useCallback(() => {
        setMostrarModalDetalhes(false);
        setTarefaParaDetalhes(null);
    }, []);

    // Handler para seleção de paciente
    const handlePacienteSelect = useCallback((paciente) => {
        setPacienteFiltrado(paciente);
    }, []);

    // Handler para resetar filtro de paciente
    const handleResetPacienteFilter = useCallback(() => {
        setPacienteFiltrado(null);
    }, []);

    return (
        <div className="p-4 sm:p-6 lg:p-8 min-h-screen"> 
            <div className="max-w-7xl mx-auto">
                {/* FilterPaciente - Posicionado no topo centralizado (oculto inicialmente) */}
                {showFilterPaciente && (
                    <FilterPaciente 
                        onPacienteSelect={handlePacienteSelect}
                        onClearSelection={handleResetPacienteFilter}
                        selectedPaciente={pacienteFiltrado}
                        position="top-center"
                    />
                )}

                {/* Header */}
                <PageTitle 
                    title="Agenda de Tarefas"
                    subtitle="Gerencie e organize todas as tarefas e atividades do dia"
                />

                {/* Botão Flutuante à Direita - Opções */}
                <div className="fixed top-20 right-4 sm:right-6 lg:right-8 z-30">
                    <div ref={novaTarefaButtonRef}>
                        <button
                            onClick={() => setShowNovaTarefaMenu(!showNovaTarefaMenu)}
                            className={`
                                flex items-center justify-center gap-2
                                px-3 py-2.5 sm:px-4 sm:py-3 rounded-lg
                                transition-all duration-200 font-medium 
                                text-xs sm:text-sm
                                border shadow-lg hover:shadow-xl
                                backdrop-blur-sm
                                ${showNovaTarefaMenu 
                                    ? 'bg-gray-100 text-gray-700 border-gray-300 border-[0.5px] shadow-xl font-semibold' 
                                    : 'bg-white/80 text-gray-600 border-gray-200 hover:border-gray-300 hover:bg-gray-50'
                                }
                            `}
                            title={showNovaTarefaMenu ? 'Ocultar menu de opções' : 'Mostrar menu de opções'}
                        >
                            <Plus className={`h-4 w-4 sm:h-5 sm:w-5 flex-shrink-0 ${showNovaTarefaMenu ? 'scale-110' : ''} transition-transform`} />
                            <span className="hidden sm:inline whitespace-nowrap">Opções</span>
                        </button>
                    </div>
                </div>

                {/* Menu Dropdown de Opções */}
                {showNovaTarefaMenu && (
                    <div ref={novaTarefaMenuRef} className="fixed top-32 sm:top-36 right-4 sm:right-6 lg:right-8 z-40 w-[calc(100%-2rem)] sm:w-auto sm:min-w-[200px]">
                        <div className="bg-white rounded-xl shadow-2xl border border-gray-200 py-2">
                            <button
                                onClick={() => {
                                    handleOpenForm(false);
                                    setShowNovaTarefaMenu(false);
                                }}
                                className="w-full flex items-center gap-3 px-4 py-2.5 hover:bg-emerald-50 transition-colors text-left"
                            >
                                <Plus className="h-5 w-5 text-emerald-600" />
                                <span className="text-sm font-medium text-gray-700">Nova Tarefa</span>
                            </button>
                            <button
                                onClick={() => {
                                    handleOpenForm(true);
                                    setShowNovaTarefaMenu(false);
                                }}
                                className="w-full flex items-center gap-3 px-4 py-2.5 hover:bg-indigo-50 transition-colors text-left"
                            >
                                <Repeat className="h-5 w-5 text-indigo-600" />
                                <span className="text-sm font-medium text-gray-700">Agend. Recorrente</span>
                            </button>
                            <div className="border-t border-gray-200 my-1"></div>
                            <button
                                onClick={() => {
                                    setShowPeriodFilter(!showPeriodFilter);
                                    setShowNovaTarefaMenu(false);
                                }}
                                className="w-full flex items-center gap-3 px-4 py-2.5 hover:bg-blue-50 transition-colors text-left"
                            >
                                <Calendar className="h-5 w-5 text-blue-600" />
                                <span className="text-sm font-medium text-gray-700">Período</span>
                            </button>
                            <button
                                onClick={() => {
                                    setShowStatusFilter(!showStatusFilter);
                                    setShowTipoFilter(false);
                                    setShowNovaTarefaMenu(false);
                                }}
                                className="w-full flex items-center gap-3 px-4 py-2.5 hover:bg-purple-50 transition-colors text-left"
                            >
                                <Tag className="h-5 w-5 text-purple-600" />
                                <span className="text-sm font-medium text-gray-700">Status</span>
                            </button>
                            <button
                                ref={tipoButtonRef}
                                onClick={() => {
                                    setShowTipoFilter(!showTipoFilter);
                                    setShowStatusFilter(false);
                                    setShowNovaTarefaMenu(false);
                                }}
                                className="w-full flex items-center gap-3 px-4 py-2.5 hover:bg-teal-50 transition-colors text-left"
                            >
                                <Tag className="h-5 w-5 text-teal-600" />
                                <span className="text-sm font-medium text-gray-700">Tipo de Tarefa</span>
                            </button>
                            <div className="border-t border-gray-200 my-1"></div>
                            <button
                                onClick={() => {
                                    setShowFilterPaciente(!showFilterPaciente);
                                    setShowNovaTarefaMenu(false);
                                }}
                                className="w-full flex items-center gap-3 px-4 py-2.5 hover:bg-orange-50 transition-colors text-left"
                            >
                                <User className="h-5 w-5 text-orange-600" />
                                <span className="text-sm font-medium text-gray-700">
                                    {showFilterPaciente ? 'Ocultar Pacientes' : 'Mostrar Pacientes'}
                                </span>
                            </button>
                        </div>
                    </div>
                )}

                {/* Filtro de Status Flutuante - Dropdown */}
                {showStatusFilter && (
                    <div ref={statusFilterRef} className="fixed top-56 sm:top-60 right-4 sm:right-6 lg:right-8 z-40 w-[calc(100%-2rem)] sm:w-auto sm:min-w-[200px]">
                        <TarefaStatusFilter
                            status={filtroStatus}
                            onStatusChange={(newStatus) => {
                                setFiltroStatus(newStatus);
                            }}
                            onClose={() => setShowStatusFilter(false)}
                        />
                    </div>
                )}

                {/* Filtro de Tipo de Tarefa Flutuante - Dropdown */}
                {showTipoFilter && (
                    <div ref={tipoFilterRef} className="fixed top-56 sm:top-60 right-4 sm:right-6 lg:right-8 z-40 w-[calc(100%-2rem)] sm:w-auto sm:min-w-[220px]">
                        <TarefaTipoFilter
                            tipos={filtroTipo}
                            onTiposChange={(newTipos) => {
                                setFiltroTipo(newTipos);
                            }}
                            onClose={() => setShowTipoFilter(false)}
                        />
                    </div>
                )}

                {/* Filtro de Calendário Flutuante - Dropdown */}
                {showPeriodFilter && (
                    <div ref={periodFilterRef} className="fixed top-32 sm:top-36 right-4 sm:right-6 lg:right-8 z-40 w-[calc(100%-2rem)] sm:w-auto sm:max-w-md">
                        <FloatingCalendarFilter
                            periodo={periodoFluxo}
                            onPeriodoChange={handlePeriodoChange}
                            startDate={rangeDatas.startDate || ''}
                            endDate={rangeDatas.endDate || ''}
                            onDateRangeChange={handleDateRangeChange}
                            noNegativeMargin={true}
                            customZIndex="z-40"
                            showOnlyContent={true}
                            onClose={() => setShowPeriodFilter(false)}
                        />
                    </div>
                )}

                {alerta && (
                    <div className="mb-4">
                        <Alert type={alerta.tipo} message={alerta.mensagem} />
                    </div>
                )}


                {/* Lista de tarefas */}
                {isLoadingTarefas ? (
                    <div className="text-center py-12 bg-white rounded-xl shadow-lg">
                        <p className="text-emerald-600 font-semibold text-lg">Carregando tarefas...</p>
                    </div>
                ) : tarefasOrdenadas.length === 0 ? (
                    <div className="text-center py-12 bg-white rounded-xl shadow-lg">
                        <Search className="h-12 w-12 mx-auto mb-4 text-gray-400" />
                        <p className="text-gray-500 text-lg">Nenhuma tarefa agendada encontrada com os filtros atuais.</p>
                    </div>
                ) : (
                    /* Grid de Cards - Responsivo: 2 colunas no mobile, 4 colunas no desktop/tablet */
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4 md:gap-6">
                        {tarefasOrdenadas.map((tarefa) => (
                            <TarefaCard
                                key={tarefa.id}
                                tarefa={tarefa}
                                getStatusReal={getStatusReal}
                                onEdit={handleEdit}
                                onDelete={handlePreparaExclusao}
                                onCancel={handlePreparaCancelamento}
                                onViewDetails={handleViewDetails}
                            />
                        ))}
                    </div>
                )}

                {/* Modais (Inalterados) */}
                <AgendaTarefaForm 
                    isOpen={mostrarModalForm} 
                    onClose={handleFormCancel} 
                    onSucesso={handleFormSuccess} 
                    tarefaParaEditar={tarefaParaEditar} 
                    onOpenConfirmacao={(data) => handleOpenConfirmacaoAcao({ ...data, tipo: 'simples' })} 
                />
                
                <AgendaTarefaFormRecorrente 
                    isOpen={mostrarModalRecorrente} 
                    onClose={handleFormCancel} 
                    onSucesso={handleFormSuccess} 
                    tarefaParaEditar={tarefaParaEditar}
                    onOpenConfirmacao={(data) => handleOpenConfirmacaoAcao({ ...data, tipo: 'recorrente' })}
                />

                <ConfirmacaoModal
                    isOpen={confirmacaoAcao.isOpen} 
                    onClose={handleCloseConfirmacaoAcao} 
                    titulo={confirmacaoAcao.titulo}
                    mensagem={confirmacaoAcao.mensagem}
                    onConfirm={confirmacaoAcao.onConfirm}
                    cancelText={confirmacaoAcao.cancelText}
                    confirmText={confirmacaoAcao.confirmText}
                    showMotivoInput={confirmacaoAcao.showMotivoInput || false}
                    motivoValue={motivoCancelamento}
                    onMotivoChange={setMotivoCancelamento}
                />

                {/* Modal de Detalhes da Tarefa */}
                <TarefaDetalhesModal
                    isOpen={mostrarModalDetalhes}
                    onClose={handleCloseDetalhes}
                    tarefa={tarefaParaDetalhes}
                    getStatusReal={getStatusReal}
                />

                {/* Modal de Cancelamento/Deleção Recorrente */}
                <ModalCancelarTarefaRecorrente
                    isOpen={showModalRecorrente}
                    onClose={() => {
                        setShowModalRecorrente(false);
                        setTarefaRecorrente(null);
                        setIsDeletingRecorrente(false);
                        setIsLoadingRecorrente(false);
                    }}
                    onConfirm={handleConfirmRecorrente}
                    tarefaInfo={tarefaRecorrente}
                    isLoading={isLoadingRecorrente}
                    isDeleting={isDeletingRecorrente}
                />
            </div>
        </div>
    );
}

export { AgendaTarefasPage };
